import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Plus, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/currency';
import { StatusBadge } from '@/components/status/StatusBadge';

interface InfluencerFeesTabProps {
  campaignInfluencers: any[];
  setAddPaymentModalOpen: (open: boolean) => void;
  budgetData: any;
  loadingPayments: boolean;
  transformedPayments: any[];
  handleMarkAsPaid: (paymentId: string) => void;
  updatePaymentStatusMutation: any;
}

export const InfluencerFeesTab: React.FC<InfluencerFeesTabProps> = ({
  campaignInfluencers,
  setAddPaymentModalOpen,
  budgetData,
  loadingPayments,
  transformedPayments,
  handleMarkAsPaid,
  updatePaymentStatusMutation,
}) => {
  const { userCountry } = useAuth();


  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Payment Management</h3>
        <Button
          onClick={() => setAddPaymentModalOpen(true)}
          className="flex items-center gap-2 bg-[#A655F8] text-white rounded-lg hover:bg-[#9544e0] transition-colors"
          disabled={campaignInfluencers.length === 0}
        >
          <Plus className="w-4 h-4" />
          Add Payment
        </Button>
      </div>

      {budgetData && budgetData.influencerPaymentDetails.length > 0 && (
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-3">Influencer Payment Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {budgetData.influencerPaymentDetails.map((detail: any, index: number) => (
              <div key={index} className="bg-white rounded-lg p-3 border">
                <div className="font-medium text-sm">{detail.influencerName}</div>
                <div className="text-xs text-gray-500 mb-2">@{detail.influencerHandle}</div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span>Total Fee:</span>
                    <span className="font-medium">{formatCurrency(detail.totalFee, userCountry)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Amount Paid:</span>
                    <span className="text-green-600 font-medium">{formatCurrency(detail.amountPaid, userCountry)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Amount Due:</span>
                    <span className="text-red-600 font-medium">{formatCurrency(detail.amountDue, userCountry)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loadingPayments ? (
        <div className="text-center py-8">
          <div>Loading payments...</div>
        </div>
      ) : transformedPayments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="mb-4">No payment records found</div>
          <p className="text-sm">Add payment records to track influencer payments</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Influencer</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Deliverables</TableHead>
              <TableHead>Content Status</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Base Fee
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Base fee excluding GST and TDS</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  Net Payable
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="text-xs">
                        <p>Base Fee + GST - TDS</p>
                        <p className="text-gray-500">Auto-calculated</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead>Due Date</TableHead>
              {/* <TableHead>Actions</TableHead> */}
            </TableRow>
          </TableHeader>
          <TableBody>
            {transformedPayments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>
                  <div>
                    <div className="font-medium">{payment.influencerName}</div>
                    <div className="text-sm text-gray-500">@{payment.handle}</div>
                  </div>
                </TableCell>
                <TableCell className="capitalize">{payment.platform}</TableCell>
                <TableCell className="text-sm">{payment.deliverables}</TableCell>
                <TableCell>
                <StatusBadge status={payment.contentStatus} />
                </TableCell>
                <TableCell className="font-medium">{formatCurrency(payment.baseFee, userCountry, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                <TableCell className="font-semibold">{formatCurrency(payment.netPayable, userCountry, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                <TableCell>
                <StatusBadge status={payment.status} />
                </TableCell>
                <TableCell className="text-sm">
                  {payment.dueDate ? new Date(payment.dueDate).toLocaleDateString() : 'Not set'}
                </TableCell>
                {/* Mark as Paid Button - Removed for now */}
                {/* <TableCell>
                  <div className="flex gap-1">
                    {payment.status !== "paid" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={() => handleMarkAsPaid(payment.id)}
                        disabled={updatePaymentStatusMutation.isPending}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Mark Paid
                      </Button>
                    )}
                  </div>
                </TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};
